// src/utils/innerCirclePrizeModal.ts

import { getAuthToken, persistAuthToken } from "../services/authService";
import { fetchInnerCircleState, fetchCurrentUser } from "../api/auth";
// `showInnerCircleWinOffCanvas` lives in the heavy `viewRecording` page
// module (bootstrap Offcanvas, libphonenumber-js, etc.). We dynamic-
// import it on demand so this util — which is reached from the main
// router setup — doesn't drag that page module into the initial
// bundle on every page load.
import { getCookie } from "../utils/cookies";
import { showRegisterPromptOffCanvas } from "../components/modals/authModal";
import { PendingInnerCircleWin } from "../types/api";
import {
	isInnerCircleResultClaimed,
	pruneClaimedInnerCircleResults,
} from "./innerCircleClaims";
import {
	clearPendingInnerCircleClaim,
	readPendingInnerCircleClaim,
} from "./innerCircleClaimContinuation";
import { decodeJwt } from "./jwt";
import { messageToast } from "../components/common/toastMessage";

const ENTRY_PAGE_PATHS = ["/enter-v2/", "/enter/", "/dashboard/entry/"];
const SESSION_STORAGE_KEY = "inner_circle_modal_show_count";
const MAX_SHOWS_PER_SESSION = 3;
const REGISTER_PROMPT_SESSION_KEY = "register_prompt_show_count";
const REGISTER_PROMPT_MAX_SHOWS = 3;

/**
 * Checks if the current path is an entry page
 * Entry pages include: /enter-v2/, /enter/, /dashboard/entry/
 */
function isEntryPage(pathname: string): boolean {
	return ENTRY_PAGE_PATHS.some((path) => pathname.startsWith(path));
}

/**
 * Gets the current show count from sessionStorage
 */
function getShowCount(): number {
	const count = sessionStorage.getItem(SESSION_STORAGE_KEY);
	return count ? parseInt(count, 10) : 0;
}

/**
 * Increments the show count in sessionStorage
 */
function incrementShowCount(): void {
	const currentCount = getShowCount();
	sessionStorage.setItem(SESSION_STORAGE_KEY, (currentCount + 1).toString());
}

/**
 * Checks if we've reached the maximum number of shows for this session
 */
function hasReachedMaxShows(): boolean {
	return getShowCount() >= MAX_SHOWS_PER_SESSION;
}

/**
 * Gets the register prompt show count from sessionStorage
 */
function getRegisterPromptShowCount(): number {
	const count = sessionStorage.getItem(REGISTER_PROMPT_SESSION_KEY);
	return count ? parseInt(count, 10) : 0;
}

/**
 * Increments the register prompt show count in sessionStorage
 */
function incrementRegisterPromptShowCount(): void {
	const currentCount = getRegisterPromptShowCount();
	sessionStorage.setItem(
		REGISTER_PROMPT_SESSION_KEY,
		(currentCount + 1).toString(),
	);
}

/**
 * Checks if we've reached the maximum number of register prompt shows for this session
 */
function hasReachedRegisterPromptMaxShows(): boolean {
	return getRegisterPromptShowCount() >= REGISTER_PROMPT_MAX_SHOWS;
}

/**
 * Opens the claim offcanvas for a specific pending Inner Circle win
 */
export async function openInnerCircleClaimModal(
	win: PendingInnerCircleWin,
	options: { phoneNumber?: string; email?: string; resume?: boolean; loginIncomplete?: boolean } = {},
): Promise<void> {
	const user = await fetchCurrentUser();
	const isTemp = Boolean(decodeJwt(user.token).payload.is_temp);
	persistAuthToken(user.token, { isTemp });

	// Store entry identifiers so the claim handler can read them without extra params
	sessionStorage.setItem("inner_circle_entry_id", String(win.entry_id));
	sessionStorage.setItem("inner_circle_entry_share_slug", win.share_slug ?? "");

	const { showInnerCircleWinOffCanvas } = await import(
		/* webpackChunkName: "page-viewRecording" */ "../pages/viewRecording"
	);
	showInnerCircleWinOffCanvas(
		win.prize_value,
		false, // No confetti for pending prize notification
		false, // Prize not yet awarded
		win.claim_expires_at, // Claim deadline set by the backend
		win.course_name ?? undefined,
		false, // Don't use generic text - we have the actual amount
		options.phoneNumber ?? user.phone_number,
		isTemp,
		win.result_id,
		options,
	);
}

let continuingClaim = false;

async function continuePendingClaim(): Promise<boolean> {
	if (continuingClaim) return true;
	continuingClaim = true;
	try {
		const claim = readPendingInnerCircleClaim();
		if (!claim || !getAuthToken()) return false;
		if (claim.returnPath !== window.location.pathname + window.location.search) return false;
		const state = await fetchInnerCircleState();
		const pendingWins = state.pending_wins ?? [];
		const win = pendingWins.find((pending) => claim.resultIds.includes(pending.result_id));
		if (!win) {
			throw new Error("Your saved prize is no longer claimable on this account. Please reopen your prize claim.");
		}
		const isTemp = Boolean(decodeJwt(getAuthToken()).payload.is_temp);
		pruneClaimedInnerCircleResults(pendingWins.map((pending) => pending.result_id));
		await openInnerCircleClaimModal(win, {
			phoneNumber: claim.phoneNumber,
			email: claim.email,
			resume: !isTemp,
			loginIncomplete: isTemp,
		});
		return true;
	} catch (error) {
		clearPendingInnerCircleClaim();
		console.error("Could not resume Inner Circle claim:", error);
		messageToast({
			message: error instanceof Error ? error.message : "Could not resume your prize claim. Please try again.",
			background: "text-bg-danger",
		});
		return true;
	} finally {
		continuingClaim = false;
	}
}

/**
 * Checks if user has a pending inner circle prize and shows the modal if appropriate
 * Shows on all pages (including home) until prize is claimed or time runs out
 */
export async function checkAndShowPendingPrizeModal(
	currentPathname?: string,
): Promise<void> {
	try {
		if (await continuePendingClaim()) return;
		// Get current pathname if not provided
		const pathname = currentPathname || window.location.pathname;

		// Don't show on entry pages (existing logic handles those)
		// Entry pages have unlimited shows, so we skip the count check for them
		if (isEntryPage(pathname)) {
			return;
		}

		// Check if user is logged in
		const token = getAuthToken();
		if (!token) {
			return;
		}

		// Check if we've reached the maximum number of shows for this session (non-entry pages only)
		if (hasReachedMaxShows()) {
			return;
		}

		// Check if offcanvas is already open to avoid duplicates
		const existingCanvas = document.getElementById("inner-circle-win-canvas");
		if (existingCanvas) {
			return; // Already showing
		}

		// Fetch user data to check for pending prize
		const inner_circle_state = await fetchInnerCircleState();
		const pendingWins = inner_circle_state?.pending_wins ?? [];
		pruneClaimedInnerCircleResults(pendingWins.map((win) => win.result_id));

		// Backend only returns wins whose claim deadline hasn't passed, newest first.
		// Skip wins already covered by a submitted claim; later wins stay claimable.
		const pendingWin = pendingWins.find(
			(win) => !isInnerCircleResultClaimed(win.result_id),
		);
		if (!pendingWin) {
			return;
		}

		await openInnerCircleClaimModal(pendingWin);

		// Increment the show count for this session (only for non-entry pages)
		incrementShowCount();
	} catch (error) {
		// Silently fail - don't interrupt user experience
		console.warn("Error checking pending prize modal:", error);
	}
}

/**
 * Checks if temp user should see register prompt on non-entry, non-home pages
 * Shows register prompt if temp user hasn't won prize and hasn't submitted email
 */
export async function checkAndShowRegisterPrompt(
	currentPathname?: string,
): Promise<void> {
	try {
		// Get current pathname if not provided
		const pathname = currentPathname || window.location.pathname;

		// Don't show on entry pages or home page (they have their own logic)
		if (isEntryPage(pathname) || pathname === "/" || pathname === "/home") {
			return;
		}

		// Check if user is logged in
		const token = getAuthToken();
		if (!token) {
			return;
		}

		// Check if user is a temp user
		const isTemp = getCookie("is_temporary") === "true";
		if (!isTemp) {
			return; // Not a temp user
		}

		// Check if user has already submitted email
		if (sessionStorage.getItem("inner_circle_email_submitted") === "true") {
			return; // Already submitted email, will receive activation email
		}

		// Check if we've reached the maximum number of shows for this session
		if (hasReachedRegisterPromptMaxShows()) {
			return;
		}

		// Check if offcanvas is already open to avoid duplicates
		const existingCanvas = document.getElementById("register-prompt-canvas");
		if (existingCanvas) {
			return; // Already showing
		}

		// Check if user has a pending inner circle prize (if so, they'll see that instead)
		const inner_circle_state = await fetchInnerCircleState();
		if (inner_circle_state?.inner_circle_prize_pending === true) {
			return; // Has pending prize, will see inner circle offcanvas instead
		}

		// Show the register prompt
		showRegisterPromptOffCanvas();

		// Increment the show count for this session
		incrementRegisterPromptShowCount();
	} catch (error) {
		// Silently fail - don't interrupt user experience
		console.warn("Error checking register prompt:", error);
	}
}
